import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Group, User } from '../../interfaces/user';
import { throwError, catchError, Observable, tap, EMPTY, finalize, of } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environments';
import { jwtDecode } from 'jwt-decode';
import { UsersService } from 'src/app/data/services/users.service';
import { StorageService } from 'src/app/core/services/storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private authBaseUrl = environment.usersBaseUrl;
  private usersService!: UsersService;

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    public router: Router,
    private injector: Injector
  ) {  }

  login(user: {email: string, password: string}): Observable<User> {
    return this.http.post<any>(`${this.authBaseUrl}/login`, user).pipe(
      tap(user => {
        // console.log("seteando sesion", user)
        this.setSession(user.token!, user.refreshToken!);
      })
    )
  }

  getNavStat() {
    return this.http.get(`https://${environment.baseUrl}/php/getNavStats.php`, { observe: 'response'}).pipe(
            catchError(error => {
              return EMPTY;
            })
          );
  }

  saveNatStat(email) {
    this.http.get(`https://${environment.baseUrl}/php/saveNavStats.php?user=${email}`).pipe(
      catchError(error => {
        console.log(error);
        return EMPTY; // Retorna un observable vacío para ignorar el error para que si falla no afecte al login
      })
    ).subscribe();
  }

  refreshSession() {
    const refreshToken = this.getRefreshToken();
    return this.http.post<any>(`${this.authBaseUrl}/refresh`, {refreshToken}).pipe(
      tap(resp => {
        this.setToken(resp.token!);
      })
    )
  }

  createUser(email: string, group: string, organizationId: string): Observable<any> {
    return this.http.post(`${this.authBaseUrl}/createUser`, {email, group, organizationId}, { headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + this.getToken(),
    }})
  }

  checkInvitationUser(email: string, organizationId: string, demo?: number): Observable<any> {
    return this.http.post(`${this.authBaseUrl}/checkInvitationUser`, {email, organizationId, ...(demo ? { demo } : {})})
  }

  register(user: User): Observable<any> {
    return this.http.post(`${this.authBaseUrl}/signUp`, user)
  }

  confirmRegister(userId: string, confirmationCode: string): Observable<any>{
    return this.http.post(`${this.authBaseUrl}/signUpConfirm`, {userId, confirmationCode})
  }

  userChangeStatus(userId: string, organizationId: string, email: string, status: string, demo?: number): Observable<any>{
    return this.http.post(`${this.authBaseUrl}/userChangeStatus`, {userId, organizationId, email, status, ...(demo ? { demo } : {})})
  }

  autoLoginDemo(userId: string, organizationId: string): Observable<any> {
    return this.http.post(`${this.authBaseUrl}/loginUserDemo`, { userId, organizationId });
  }

  checkAccountStatus(userId: string): Observable<any>{
    return this.http.post(`${this.authBaseUrl}/checkAccountStatus`, {userId})
  }

  sendConfirmCode(userId: string): Observable<any> {
    return this.http.post(`${this.authBaseUrl}/sendConfirmCode`, {userId})
  }

  passwordChange(email: string, password: string, newpassword: string): Observable<any> {
    return this.http.post(`${this.authBaseUrl}/pwdChange`, {email, password, newpassword})
  }

  passwordReset(email: string): Observable<any> {
    return this.http.post(`${this.authBaseUrl}/pwdReset`, {email})
  }

  passwordConfirm(email: string, password: string, confirmationCode: string): Observable<any> {
    return this.http.post(`${this.authBaseUrl}/pwdConfirm`, {email, password, confirmationCode})
  }

  setSession(token: string, refreshToken: string) {
    this.storage.set("token", token);
    this.storage.set("refreshToken", refreshToken);
  }

  setToken(token: string) {
    this.storage.set("token", token);
  }

  getToken(): string {
    return this.storage.get("token");
  }

  getUserId(): string {
    const token = this.storage.get("token");
    if (token) {
      return jwtDecode(token)['username'];
    }
    return '';
  }

  getRefreshToken(): string {
    return this.storage.get("refreshToken");
  }

  authenticated(): boolean {
    return !!this.storage.get("token");
  }

  private getUsersService(): UsersService {
    if (!this.usersService) {
      this.usersService = this.injector.get(UsersService);
    }
    return this.usersService;
  }

  logout(lessonId?: string, email?: string, organizationId?: string, register?: boolean): void {
    const refreshToken = this.getRefreshToken();
    const logoutUrl = `${this.authBaseUrl}/logout`;
  
    this.deleteSession();
  
    this.http.post<any>(logoutUrl, { refreshToken }).pipe(
      tap(() => {
        this.getUsersService().clearUserData();
      }),
      catchError((error) => {
        if (error.status === 400) {
          console.warn('El token de refresco no era válido:', error.message);
          return throwError(() => new Error('Token inválido'));
        }
        console.error('Error al cerrar sesión:', error);
        return throwError(() => new Error('Error al cerrar sesión'));
      }),
      finalize(() => {
        if(lessonId && email && organizationId){
          if(register){
            this.router.navigateByUrl(`register/levelab?lessonId=${lessonId}&email=${email}$&organizationId=${organizationId}`);
          }else{
            this.router.navigateByUrl(`/login/levelab?lessonId=${lessonId}&email=${email}`);
          }
        } else if (organizationId && email) {
          if (register) {
            this.router.navigateByUrl(`register/levelab?organizationId=${organizationId}&email=${email}`);
          } else {
            this.router.navigateByUrl(`/login?&email=${email}`);
          }
        }else{
          this.router.navigateByUrl('login');
        }
      })
    ).subscribe();
  }

  private deleteSession(): void {
    this.storage.clearAuthData();
  }

  clearSession(): void { //Logout sin redirección (por si no se quiere redirigir a login, como en el caso de moodle)
    const refreshToken = this.getRefreshToken();
    const logoutUrl = `${this.authBaseUrl}/logout`;

    this.deleteSession();

    this.http.post<any>(logoutUrl, { refreshToken }).pipe(
      tap(() => {
        this.getUsersService().clearUserData();
      }),
      catchError((error) => {
        console.log(error);
        return of(null);
      })
    ).subscribe();
  }
}